import React from 'react';
import {
    Instagram,
    Youtube,
    Facebook,
    Linkedin,
    Search,
} from 'lucide-react';
import { TiktokIcon } from '../../../external/tiktok-icon/TiktokIcon';
import { GoogleDetails, SocialMediaProfilesTypes } from '../../../../types';
import type { CSSProperties } from 'react';

type SocialFooterBlockProps = {
    BUSINESS_NAME?: string;
    socialMediaProfiles: SocialMediaProfilesTypes;
    googleDetails: GoogleDetails;
    hideHandles: boolean;
    tokens: {
        socialItemBackground: string;
        socialTextColor: string;
        socialHoverColor: string;
        iconColor: string;
    };
};

type SocialItem = {
    title: string;
    link?: string;
    handle?: string;
    Icon: React.ElementType;
    isTikTok?: boolean;
};

const styles: Record<string, CSSProperties> = {
    wrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
    item: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '8px 12px',
        borderRadius: '8px',
        textDecoration: 'none',
        transition: 'color 160ms ease, background-color 160ms ease',
        fontSize: '14px',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
    },
    iconWrap: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    label: {
        margin: 0,
        textDecoration: 'none',
    },
};

const SocialFooterBlock = ({
    BUSINESS_NAME,
    socialMediaProfiles,
    googleDetails,
    hideHandles,
    tokens,
}: SocialFooterBlockProps) => {
    const {
        instaHandle,
        instaLink,
        youTubeHandle,
        youTubeLink,
        tikTokHandle,
        tikTokLink,
        facebookHandle,
        facebookLink,
        linkedInHandle,
        linkedInLink,
    } = socialMediaProfiles;

    const { googleHandle, googleLink } = googleDetails;

    const items: SocialItem[] = [
        { title: 'Instagram', link: instaLink, handle: instaHandle, Icon: Instagram },
        { title: 'YouTube', link: youTubeLink, handle: youTubeHandle, Icon: Youtube },
        { title: 'TikTok', link: tikTokLink, handle: tikTokHandle, Icon: TiktokIcon, isTikTok: true },
        { title: 'Facebook', link: facebookLink, handle: facebookHandle, Icon: Facebook },
        { title: 'LinkedIn', link: linkedInLink, handle: linkedInHandle, Icon: Linkedin },
        { title: 'Google', link: googleLink, handle: googleHandle, Icon: Search },
    ];

    return (
        <div style={styles.wrap}>
            {items.map(({ title, link, handle, Icon, isTikTok }) => {
                if (!handle || !link) return null;

                const href = `${link}${handle}`;
                const label = hideHandles
                    ? title
                    : title === 'Instagram'
                        ? `@${handle}`
                        : handle;

                return (
                    <a
                        key={`${title}-${handle}`}
                        href={href}
                        target="_blank"
                        rel="nofollow noreferrer"
                        title={BUSINESS_NAME ? `${BUSINESS_NAME} on ${title}` : title}
                        className="footer-social-link"
                        style={{
                            ...styles.item,
                            background: tokens.socialItemBackground,
                            color: tokens.socialTextColor,
                        }}
                    >
                        <span style={styles.iconWrap} aria-hidden="true">
                            {isTikTok ? (
                                <TiktokIcon color={tokens.iconColor} className="footer-social-icon" />
                            ) : (
                                <Icon className="footer-social-icon" />
                            )}
                        </span>
                        <span style={styles.label}>{label}</span>
                    </a>
                );
            })}
        </div>
    );
};

export default SocialFooterBlock;
